// src/api/teamService.js
// Team management service for invitations and member management

import { apiFetch } from "./http";
import { API_ENDPOINTS } from "../config";

/**
 * @typedef {Object} Invite
 * @property {number} id
 * @property {string} code
 * @property {string|null} email
 * @property {"agent"|"admin"} role
 * @property {number} max_uses
 * @property {number} uses
 * @property {string|null} expires_at
 * @property {boolean} is_active
 * @property {string} created_at
 */

/**
 * @typedef {Object} TeamMember
 * @property {number} id
 * @property {string} name
 * @property {string} email
 * @property {"agent"|"admin"} role
 * @property {string} created_at
 */

/**
 * @typedef {Object} CreateInviteParams
 * @property {string} [email] - If specified, only this email can use the code
 * @property {"agent"|"admin"} [role="agent"] - Role for the new user
 * @property {number} [max_uses=1] - How many times the code can be used
 * @property {number} [expires_in_days=7] - Days until expiration (null = never)
 */

export const teamService = {
  /**
   * Get team members for the current organization
   * @returns {Promise<TeamMember[]>} List of team members
   */
  async getMembers() {
    const response = await apiFetch(API_ENDPOINTS.TEAM_MEMBERS);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to get team members");
    }

    return response.json();
  },

  /**
   * Create an invitation code
   * @param {CreateInviteParams} params - Invitation parameters
   * @returns {Promise<Invite>} Created invitation with code
   */
  async createInvite(params = {}) {
    const {
      email,
      role = "agent",
      max_uses = 1,
      expires_in_days = 7,
    } = params;

    const body = {
      role,
      max_uses,
      expires_in_days,
    };

    // Only include email if provided
    if (email) {
      body.email = email;
    }

    const response = await apiFetch(API_ENDPOINTS.INVITES, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to create invitation");
    }

    return response.json();
  },

  /**
   * Get all invitations for the organization
   * @returns {Promise<{invites: Invite[], total: number}>} List of invitations
   */
  async getInvites() {
    const response = await apiFetch(API_ENDPOINTS.INVITES);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to get invitations");
    }

    return response.json();
  },

  /**
   * Deactivate an invitation code
   * @param {number} inviteId - ID of the invitation to deactivate
   * @returns {Promise<{status: string, message: string}>} Deactivation result
   */
  async deleteInvite(inviteId) {
    const response = await apiFetch(`${API_ENDPOINTS.INVITES}/${inviteId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to deactivate invitation");
    }

    return response.json();
  },

  /**
   * Regenerate an invitation code (creates new code, resets uses)
   * @param {number} inviteId - ID of the invitation to regenerate
   * @returns {Promise<Invite>} Updated invitation with new code
   */
  async regenerateInvite(inviteId) {
    const response = await apiFetch(
      `${API_ENDPOINTS.INVITES}/${inviteId}/regenerate`,
      {
        method: "POST",
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to regenerate invitation");
    }

    return response.json();
  },

  /**
   * Remove a team member from the organization
   * @param {number} memberId - ID of the member to remove
   * @returns {Promise<{status: string, message: string}>} Removal result
   */
  async removeMember(memberId) {
    const response = await apiFetch(
      `${API_ENDPOINTS.TEAM_MEMBERS}/${memberId}`,
      {
        method: "DELETE",
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Failed to remove team member");
    }

    return response.json();
  },

  // Legacy alias for backwards compatibility
  sendInvite: (email, role) =>
    teamService.createInvite({ email, role }),
  getPendingInvites: () => teamService.getInvites(),
  cancelInvite: (inviteId) => teamService.deleteInvite(inviteId),
};
